import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, TrendingUp, BarChart3, Search, BookOpen, Clock, ArrowRight, FileText, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { articlesData } from '../data/articles';
import { PDFViewer } from '../components/PDFViewer';
import axios from 'axios';

// Use relative API path for same-origin requests (works in all environments)
const API = '/api';

export const ArticlesPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [amazingReads, setAmazingReads] = useState([]);
  const [loadingReads, setLoadingReads] = useState(true);
  const [selectedPDF, setSelectedPDF] = useState(null);

  const categories = [
    { id: 'all', name: 'All Articles' },
    { id: 'core', name: 'Core FP&A' },
    { id: 'capital', name: 'Capital & Growth' },
    { id: 'process', name: 'Process & Simplification' },
    { id: 'profitability', name: 'Profitability' },
    { id: 'future', name: 'Future of Finance' }
  ];

  useEffect(() => {
    fetchAmazingReads();
  }, []);

  const fetchAmazingReads = async () => {
    try {
      const response = await axios.get(`${API}/amazing-reads`);
      setAmazingReads(response.data);
    } catch (error) {
      console.error('Error fetching amazing reads:', error);
    } finally {
      setLoadingReads(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const articles = [
    // Core FP&A & Strategic Finance
    {
      id: 1,
      title: 'Stop Budgeting, Start Forecasting: The Modern FP&A Mindset Shift',
      description: 'Traditional budgeting is dead. Learn why forward-looking forecasts drive better decisions than annual budgets.',
      category: 'core',
      readTime: '8 min read',
      featured: true
    },
    {
      id: 2,
      title: 'The SaaS Magic Number and LTV/CAC: Financial Models Every Founder Needs',
      description: 'Master the metrics that determine SaaS viability. Build models that investors actually want to see.',
      category: 'core',
      readTime: '10 min read',
      featured: true
    },
    {
      id: 3,
      title: 'Beyond Excel: When is it Time for Dedicated FP&A Software?',
      description: 'Excel has limits. Discover when complexity demands purpose-built tools and what to look for.',
      category: 'core',
      readTime: '7 min read',
      featured: false
    },
    {
      id: 4,
      title: 'How to Build a Zero-Based Budgeting (ZBB) Model for Startup Scalability',
      description: 'Start from zero, justify every dollar. Build budgets that scale without bloat.',
      category: 'core',
      readTime: '12 min read',
      featured: false
    },
    {
      id: 5,
      title: 'Activity-Based Costing (ABC) vs. Traditional Costing: A Profitability Deep Dive',
      description: 'Understand true product costs. ABC reveals profitability that traditional methods hide.',
      category: 'core',
      readTime: '9 min read',
      featured: false
    },
    {
      id: 6,
      title: 'The 90-Day FP&A Plan: Your Roadmap to Financial Clarity as a New CFO',
      description: 'First 90 days define success. A proven framework for new finance leaders.',
      category: 'core',
      readTime: '11 min read',
      featured: false
    },
    {
      id: 7,
      title: '3 Essential Financial Scenarios: A Guide to Building Dynamic What-If Models',
      description: 'Base, worst, best. Build scenario models that prepare you for anything.',
      category: 'core',
      readTime: '10 min read',
      featured: false
    },

    // Capital & Growth
    {
      id: 8,
      title: 'The Investor-Ready Financial Model: 5 Things VCs Look For in Your Projections',
      description: 'VCs reject 99% of pitches. Your financial model is often why. Learn what they actually scrutinize.',
      category: 'capital',
      readTime: '14 min read',
      featured: true
    },
    {
      id: 9,
      title: 'Bootstrapped to Series A: How to Model Your Operating Runway',
      description: 'Know exactly when you run out of money. Model runway with precision, raise at strength.',
      category: 'capital',
      readTime: '9 min read',
      featured: false
    },
    {
      id: 10,
      title: 'The Truth About Dilution: Modeling the Impact of Your Next Funding Round',
      description: 'Every round costs ownership. Model dilution before you negotiate terms.',
      category: 'capital',
      readTime: '11 min read',
      featured: false
    },
    {
      id: 11,
      title: 'Forecasting Headcount: How to Build a Scalable People Plan into Your Budget',
      description: 'People are your biggest cost. Model headcount growth that scales revenue, not chaos.',
      category: 'capital',
      readTime: '10 min read',
      featured: false
    },
    {
      id: 12,
      title: 'The Hidden Cost of Growth: Modeling Working Capital Requirements',
      description: 'Growth consumes cash. Model working capital needs before they choke your runway.',
      category: 'capital',
      readTime: '12 min read',
      featured: false
    },

    // Process & Simplification
    {
      id: 13,
      title: 'The Budget vs. Actuals Reconciliation Playbook: Achieving 99% Alignment',
      description: 'Budget-to-actuals variance should not be a mystery. A systematic approach to perfect alignment.',
      category: 'process',
      readTime: '8 min read',
      featured: false
    },
    {
      id: 14,
      title: 'Automating Financial Reporting: Free Your FP&A Team from Data Drudgery',
      description: 'Manual reporting wastes 60% of FP&A time. Automate the tedious, focus on insight.',
      category: 'process',
      readTime: '9 min read',
      featured: false
    },
    {
      id: 15,
      title: 'Chart of Accounts Best Practices for Modern, Scalable Reporting',
      description: 'Your chart of accounts is your financial foundation. Structure it for clarity, not confusion.',
      category: 'process',
      readTime: '7 min read',
      featured: false
    },
    {
      id: 16,
      title: 'Why Your Financial Model Keeps Breaking (And How to Build an Adaptable Framework)',
      description: 'Brittle models break with every pivot. Build frameworks that bend, not shatter.',
      category: 'process',
      readTime: '10 min read',
      featured: false
    },
    {
      id: 17,
      title: 'Simplifying Complex Models: The Principle of One Source of Truth in FP&A',
      description: 'Multiple versions of truth create chaos. Establish one source, eliminate confusion.',
      category: 'process',
      readTime: '8 min read',
      featured: false
    },

    // Profitability & Value-Add
    {
      id: 18,
      title: 'The Art of P&L Decomposition: Finding the True Drivers of Profitability',
      description: 'Surface-level P&L analysis misses leverage points. Decompose to discover real profit drivers.',
      category: 'profitability',
      readTime: '11 min read',
      featured: false
    },
    {
      id: 19,
      title: 'Driving Decisions with Data: How to Create a High-Impact FP&A Dashboard',
      description: 'Dashboards should drive action, not just display data. Design for decision velocity.',
      category: 'profitability',
      readTime: '9 min read',
      featured: false
    },
    {
      id: 20,
      title: 'From Cost Center to Strategic Partner: Elevating the FP&A Function',
      description: 'Finance is not just compliance. Transform FP&A into a strategic growth engine.',
      category: 'profitability',
      readTime: '10 min read',
      featured: false
    },
    {
      id: 21,
      title: 'Understanding Variance Analysis: Translating the Why Behind the Numbers',
      description: 'Numbers change. The why matters more. Master variance analysis for actionable insight.',
      category: 'profitability',
      readTime: '8 min read',
      featured: false
    },
    {
      id: 22,
      title: 'Product Profitability Analysis: A Step-by-Step Guide for CEOs',
      description: 'Not all revenue is equal. Identify which products drive profit, which drain it.',
      category: 'profitability',
      readTime: '12 min read',
      featured: false
    },

    // Future of Finance
    {
      id: 23,
      title: 'AI in FP&A: Practical Use Cases for Forecasting and Anomaly Detection',
      description: 'AI is not hype in finance anymore. Real applications that improve forecast accuracy today.',
      category: 'future',
      readTime: '13 min read',
      featured: false
    },
    {
      id: 24,
      title: 'Beyond the Quarter: Long-Range Planning Best Practices for Sustained Value',
      description: 'Quarterly thinking kills long-term value. Build strategic planning that endures.',
      category: 'future',
      readTime: '11 min read',
      featured: false
    },
    {
      id: 25,
      title: 'The Fractional CFO Model: Maximizing Expertise Without the Overhead',
      description: 'Full-time CFOs cost $300K+. Fractional gives you expertise at a fraction of the cost.',
      category: 'future',
      readTime: '9 min read',
      featured: false
    }
  ];

  const filteredArticles = articles.filter(article => {
    const matchesCategory = selectedCategory === 'all' || article.category === selectedCategory;
    const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         article.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const featuredArticles = articles.filter(a => a.featured);

  const handlePDFClick = (pdf) => {
    setSelectedPDF(pdf);
  };

  const handleClosePDF = () => {
    setSelectedPDF(null);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* PDF Viewer Modal */}
      {selectedPDF && (
        <PDFViewer pdf={selectedPDF} onClose={handleClosePDF} />
      )}

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 py-4 sm:py-6 bg-white/80 backdrop-blur-lg border-b border-gray-200">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity">
            <div className="relative">
              <TrendingUp className="h-5 w-5 sm:h-7 sm:w-7 text-[#C9A64A]" />
              <BarChart3 className="h-3 w-3 sm:h-4 sm:w-4 text-[#C9A64A] absolute -bottom-1 -right-1" />
            </div>
            <span className="text-lg sm:text-xl font-bold text-[#1A1A1C] tracking-tight">Virtual<span className="text-[#C9A64A]">CFO</span></span>
          </Link>
          <Link to="/">
            <Button variant="outline" className="gap-2 text-sm">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-24 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 bg-gradient-to-b from-[#0F1A2A] to-[#1A1A1C] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 sm:gap-3 mb-6 sm:mb-8 px-4 sm:px-6 py-2 sm:py-3 bg-[#C9A64A]/20 rounded-full border border-[#C9A64A]/30">
              <BookOpen className="h-4 w-4 sm:h-5 sm:w-5 text-[#C9A64A]" />
              <span className="text-[#C9A64A] text-xs sm:text-sm font-bold tracking-widest uppercase">Insights & Guides</span>
            </div>
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
              Financial Clarity,<br />
              <span className="text-gradient">Not Chaos.</span>
            </h1>
            <p className="text-base sm:text-xl md:text-2xl text-gray-300 font-light leading-relaxed">
              Long-form guides on FP&A, strategic finance, and building systems that scale. 
              No fluff. No theory. Just practical advice from operators.
            </p>
          </div>
        </div>
      </section>

      {/* Amazing Reads Section - AT THE TOP */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-white to-[#F8F8F8]">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 sm:mb-12 text-center">
            <div className="inline-flex items-center gap-2 sm:gap-3 mb-4 px-4 sm:px-6 py-2 bg-red-500/10 rounded-full border border-red-500/20">
              <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-red-500" />
              <span className="text-red-500 text-xs sm:text-sm font-bold tracking-widest uppercase">Premium Reads</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#1A1A1C] mb-3">
              Amazing Reads
            </h2>
            <p className="text-base sm:text-lg text-[#5A6270] max-w-2xl mx-auto">
              Deep-dive playbooks and frameworks from industry leaders. Read directly in your browser with our embedded viewer.
            </p>
          </div>

          {loadingReads ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#C9A64A] border-t-transparent"></div>
              <p className="text-[#5A6270] mt-4">Loading amazing reads...</p>
            </div>
          ) : amazingReads.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <p className="text-[#5A6270]">No reads available yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {amazingReads.map((read) => (
                <button
                  key={read.id}
                  onClick={() => handlePDFClick(read)}
                  className="group block bg-white border border-gray-200 rounded-2xl overflow-hidden hover:shadow-xl hover:border-[#C9A64A] transition-all duration-300 text-left w-full"
                >
                  <div className="p-6">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-red-500 to-pink-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <FileText className="h-6 w-6 text-white" />
                      </div>
                      <div className="flex-1">
                        {read.tags && read.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mb-2">
                            {read.tags.slice(0, 2).map((tag, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-[#C9A64A]/10 text-[#C9A64A] text-xs rounded-full font-semibold">
                                {tag}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <h3 className="text-xl font-bold text-[#1A1A1C] mb-3 group-hover:text-[#C9A64A] transition-colors leading-tight">
                      {read.title}
                    </h3>

                    {read.description && (
                      <p className="text-sm text-[#5A6270] mb-4 line-clamp-3">
                        {read.description}
                      </p>
                    )}

                    <div className="flex items-center justify-between text-xs text-[#5A6270] mb-4">
                      <div className="flex items-center gap-3">
                        <span>{formatFileSize(read.file_size)}</span>
                        {read.author && (
                          <>
                            <span>•</span>
                            <span>{read.author}</span>
                          </>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        <span>{read.view_count}</span>
                      </div>
                    </div>

                    <div className="w-full bg-gradient-to-r from-red-500/10 to-pink-500/10 text-red-500 hover:from-red-500 hover:to-pink-500 hover:text-white font-semibold py-2 px-4 rounded-lg transition-all text-center flex items-center justify-center gap-2">
                      Read Now
                      <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Featured Articles */}
      {featuredArticles.length > 0 && (
        <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-b from-[#F8F8F8] to-white">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A1A1C] mb-3 sm:mb-4">Featured Guides</h2>
              <div className="h-1 w-16 sm:w-24 bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] rounded-full" />
            </div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
              {featuredArticles.map(article => (
                <Link key={article.id} to={`/articles/${Object.keys(articlesData).find(key => articlesData[key].id === article.id)}`}>
                  <Card 
                    className="glass-card hover:shadow-xl transition-all duration-300 cursor-pointer border-2 border-[#C9A64A]/20 group overflow-hidden h-full"
                  >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C9A64A]/0 to-[#C9A64A]/0 group-hover:from-[#C9A64A]/10 group-hover:to-transparent transition-all duration-300" />
                  <CardHeader className="relative z-10 p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-3 sm:mb-4">
                      <span className="text-xs font-bold text-[#C9A64A] uppercase tracking-wider px-2 sm:px-3 py-1 bg-[#C9A64A]/10 rounded-full">
                        Featured
                      </span>
                      <div className="flex items-center gap-1 sm:gap-2 text-[#5A6270] text-xs sm:text-sm">
                        <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                        {article.readTime}
                      </div>
                    </div>
                    <CardTitle className="text-lg sm:text-2xl font-bold text-[#1A1A1C] group-hover:text-[#C9A64A] transition-colors leading-tight">
                      {article.title}
                    </CardTitle>
                    <CardDescription className="text-[#5A6270] mt-2 sm:mt-3 leading-relaxed text-sm sm:text-base">
                      {article.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-4 sm:p-6 pt-0">
                    <Button 
                      variant="ghost" 
                      className="text-[#C9A64A] hover:text-[#D4B76A] hover:bg-[#C9A64A]/10 font-semibold group/btn text-sm"
                    >
                      Read Article
                      <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Search and Filter */}
      <section className="py-8 sm:py-12 px-4 sm:px-6 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col gap-4 sm:gap-6">
            {/* Search */}
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#5A6270]" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 border-2 border-gray-300 focus:border-[#C9A64A]"
              />
            </div>

            {/* Category Filter */}
            <div className="flex gap-2 sm:gap-3 flex-wrap">
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-3 sm:px-6 py-2 rounded-full font-semibold transition-all duration-300 text-xs sm:text-sm ${
                    selectedCategory === cat.id
                      ? 'bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] shadow-lg'
                      : 'bg-gray-100 text-[#5A6270] hover:bg-gray-200'
                  }`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* All Articles Grid */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#1A1A1C]">
              {selectedCategory === 'all' ? 'All Articles' : categories.find(c => c.id === selectedCategory)?.name}
            </h2>
            <p className="text-[#5A6270] mt-2 text-sm sm:text-base">{filteredArticles.length} articles</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {filteredArticles.map(article => (
              <Link key={article.id} to={`/articles/${Object.keys(articlesData).find(key => articlesData[key].id === article.id)}`}>
                <Card 
                  className="glass-card hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200 group h-full"
                >
                <CardHeader className="p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-2 sm:mb-3">
                    <span className="text-xs font-semibold text-[#5A6270] uppercase tracking-wider">
                      {categories.find(c => c.id === article.category)?.name}
                    </span>
                    <div className="flex items-center gap-1 sm:gap-2 text-[#5A6270] text-xs sm:text-sm">
                      <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                      {article.readTime}
                    </div>
                  </div>
                  <CardTitle className="text-lg sm:text-xl font-bold text-[#1A1A1C] group-hover:text-[#C9A64A] transition-colors leading-tight">
                    {article.title}
                  </CardTitle>
                  <CardDescription className="text-[#5A6270] mt-2 leading-relaxed text-sm">
                    {article.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-4 sm:p-6 pt-0">
                  <Button 
                    variant="ghost" 
                    className="text-[#C9A64A] hover:text-[#D4B76A] hover:bg-[#C9A64A]/10 font-semibold group/btn text-sm"
                  >
                    Read More
                    <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-20 px-4 sm:px-6 bg-gradient-to-r from-[#0F1A2A] to-[#1A1A1C] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-6">
            Stop reading. Start building.
          </h2>
          <p className="text-base sm:text-xl text-gray-300 font-light mb-8 sm:mb-10 leading-relaxed px-4">
            These guides show you what&apos;s possible.<br />
            Our Virtual CFO service actually builds it for you.
          </p>
          <Link to="/">
            <Button 
              size="lg"
              className="bg-gradient-to-r from-[#C9A64A] to-[#D4B76A] text-[#0F1A2A] hover:scale-105 transition-all duration-300 px-10 py-6 text-lg font-bold rounded-xl"
            >
              Work with Virtual CFO
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
